import { Modal, message } from "antd";
import { useState, useEffect, useCallback, useRef } from "react";
import FolderSidebar from "./FolderSidebar";
import ImageGrid from "./ImageGrid";
import UploadButton from "./UploadButton";
import { DeleteOutlined } from "@ant-design/icons";
import {
    getPhotoLibraries,
    uploadPhotoLibrary,
    deletePhotoLibrary,
    getPhotoDirectories,
    createPhotoDirectory,
    updatePhotoDirectory,
    deletePhotoDirectory,
    PhotoItem,
} from "../../api/PhotoLibrary";

interface UiImage {
    id: string;        // String(id) cho UI
    rawId: number;     // id số để gọi API
    url: string;
    name: string;
}

const PAGE_SIZE = 30;

const mapPhoto = (p: PhotoItem): UiImage => ({
    id: String(p.id),
    rawId: p.id,
    url: p.image,
    name: p.name,
});

export default function MediaModal({
    open,
    onClose,
    onSelectImages,
}: {
    open: boolean;
    onClose: () => void;
    onSelectImages: (images: any[]) => void;
}) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [search, setSearch] = useState("");

    const [folders, setFolders] = useState<{ id: string | number; name: string }[]>([{ id: "all", name: "Tất cả" }]);
    const [activeFolder, setActiveFolder] = useState<string | number>("all");

    const [images, setImages] = useState<UiImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [uploading, setUploading] = useState(false);

    const pageRef = useRef(1);
    const totalRef = useRef(0);
    const loadedRef = useRef(0);
    const loadingMoreRef = useRef(false);

    // ----- Thư mục -----
    const loadDirectories = useCallback(async () => {
        const dirs = await getPhotoDirectories();
        setFolders([{ id: "all", name: "Tất cả" }, ...dirs]);
    }, []);

    // ----- Ảnh -----
    const buildParams = (page: number) => ({
        name: search.trim() || undefined,
        directory_id: activeFolder !== "all" ? Number(activeFolder) : undefined,
        limit: PAGE_SIZE,
        page,
    });

    const fetchImages = useCallback(async () => {
        setLoading(true);
        pageRef.current = 1;
        const res = await getPhotoLibraries(buildParams(1));
        if (res.status && res.data) {
            const items = res.data.items.map(mapPhoto);
            totalRef.current = res.data.total ?? items.length;
            loadedRef.current = items.length;
            setImages(items);
        } else {
            totalRef.current = 0;
            loadedRef.current = 0;
            setImages([]);
        }
        setLoading(false);
    }, [search, activeFolder]);

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current) return;
        if (loadedRef.current >= totalRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const next = pageRef.current + 1;
        const res = await getPhotoLibraries(buildParams(next));
        if (res.status && res.data) {
            const items = res.data.items.map(mapPhoto);
            pageRef.current = next;
            totalRef.current = res.data.total ?? totalRef.current;
            setImages((prev) => {
                const ids = new Set(prev.map((i) => i.id));
                const merged = [...prev, ...items.filter((i) => !ids.has(i.id))];
                loadedRef.current = merged.length;
                return merged;
            });
        }
        loadingMoreRef.current = false;
        setLoadingMore(false);
    }, [search, activeFolder]);

    const handleGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
            if (!loadingMoreRef.current && loadedRef.current < totalRef.current) loadMore();
        }
    };

    // Tải thư mục khi mở modal
    useEffect(() => {
        if (open) {
            loadDirectories();
            setSelectedIds([]);
        }
    }, [open, loadDirectories]);

    // Tải ảnh khi mở / đổi thư mục / tìm kiếm (debounce search)
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => { fetchImages(); }, search ? 400 : 0);
        return () => clearTimeout(timer);
    }, [open, fetchImages]);

    // ----- Upload -----
    const handleUpload = async (file: File) => {
        if (!file.type.startsWith("image/")) {
            message.error("Vui lòng chọn tệp ảnh");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            message.error("Tệp vượt quá 5MB");
            return;
        }
        setUploading(true);
        const name = `${Date.now()}_${file.name}`;
        const res = await uploadPhotoLibrary({
            image: file,
            name,
            directory_id: activeFolder !== "all" ? Number(activeFolder) : undefined,
        });
        setUploading(false);
        if (res.status) {
            message.success("Tải lên thành công");
            fetchImages();
        } else {
            message.error(res.msg || "Tải lên thất bại");
        }
    };

    // ----- Delete -----
    const handleDelete = (id: string) => {
        const img = images.find((i) => i.id === id);
        if (!img) return;
        Modal.confirm({
            title: "Xóa ảnh?",
            content: "Hành động này không thể hoàn tác.",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            centered: true,
            onOk: async () => {
                const res = await deletePhotoLibrary(img.rawId);
                if (res.status) {
                    setImages((prev) => prev.filter((i) => i.id !== id));
                    setSelectedIds((prev) => prev.filter((i) => i !== id));
                    message.success("Đã xóa");
                } else {
                    message.error(res.msg || "Xóa thất bại");
                }
            },
        });
    };

    const deleteSelected = () => {
        if (selectedIds.length === 0) return;
        Modal.confirm({
            title: `Xóa ${selectedIds.length} ảnh?`,
            content: "Hành động này không thể hoàn tác.",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            centered: true,
            onOk: async () => {
                const targets = images.filter((i) => selectedIds.includes(i.id));
                const results = await Promise.all(targets.map((i) => deletePhotoLibrary(i.rawId)));
                const okIds = targets.filter((_, idx) => results[idx].status).map((i) => i.id);
                if (okIds.length) {
                    setImages((prev) => prev.filter((i) => !okIds.includes(i.id)));
                    setSelectedIds((prev) => prev.filter((i) => !okIds.includes(i)));
                    message.success(`Đã xóa ${okIds.length} ảnh`);
                }
                if (okIds.length < targets.length) message.error("Một số ảnh xóa không thành công");
            },
        });
    };

    // ----- Folder CRUD -----
    const handleCreateFolder = async (name: string) => {
        const res = await createPhotoDirectory(name);
        if (res.status) {
            message.success("Đã tạo thư mục");
            loadDirectories();
        } else {
            message.error("Tạo thư mục thất bại");
        }
    };

    const handleRenameFolder = async (id: string | number, name: string) => {
        const res = await updatePhotoDirectory(Number(id), name);
        if (res.status) {
            loadDirectories();
        } else {
            message.error("Đổi tên thất bại");
        }
    };

    const handleDeleteFolder = async (id: string | number) => {
        const res = await deletePhotoDirectory(Number(id));
        if (res.status) {
            if (activeFolder === id) setActiveFolder("all");
            message.success("Đã xóa thư mục");
            loadDirectories();
        } else {
            message.error(res.msg || "Chỉ xóa được thư mục rỗng");
        }
    };

    // ----- Chọn ảnh -----
    const handleSelectImages = () => {
        const selected = images.filter((img) => selectedIds.includes(img.id));
        const mapped = selected.map((img) => ({
            file: null,
            preview: img.url,
            type: "image/*",
        }));
        onSelectImages(mapped);
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1000}
            className="dark:bg-[#1f1f1f] custom-modal-close-button"
        >
            <div className="flex h-[600px] sm:flex-row flex-col">
                {/* LEFT */}
                <FolderSidebar
                    folders={folders}
                    activeFolder={activeFolder}
                    setActiveFolder={setActiveFolder}
                    onCreateFolder={handleCreateFolder}
                    onRenameFolder={handleRenameFolder}
                    onDeleteFolder={handleDeleteFolder}
                />

                {/* RIGHT */}
                <div className="flex-1 sm:p-4 !pr-0 !pb-0 flex flex-col pl-0 pt-6">
                    {/* search */}
                    <input
                        placeholder="Tìm kiếm ảnh bằng tên"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="mb-4 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800"
                    />

                    {/* grid */}
                    <div className="flex-1 overflow-y-auto" onScroll={handleGridScroll}>
                        {loading ? (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                                Đang tải...
                            </div>
                        ) : images.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                                Chưa có ảnh
                            </div>
                        ) : (
                            <>
                                <ImageGrid
                                    images={images}
                                    selectedIds={selectedIds}
                                    setSelectedIds={setSelectedIds}
                                    onDelete={handleDelete}
                                />
                                {loadingMore && (
                                    <div className="py-3 text-center text-gray-400 text-sm">Đang tải thêm...</div>
                                )}
                            </>
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex items-center justify-between mt-3 sm:flex-row flex-col gap-2">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={deleteSelected}
                                disabled={selectedIds.length === 0}
                                className="p-2 border rounded-lg hover:bg-red-100 dark:hover:bg-red-900 w-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <DeleteOutlined />
                            </button>

                            <span className="text-sm">
                                Đã chọn {selectedIds.length} ảnh
                            </span>
                        </div>

                        <div className="flex gap-2">
                            <UploadButton onUpload={handleUpload} uploading={uploading} />
                            <button
                                onClick={handleSelectImages}
                                disabled={selectedIds.length === 0}
                                className="px-4 py-2 bg-[#3e91ab] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Chọn
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
